import React, { useEffect, useId, useRef } from 'react';
import { AlertTriangle, Info, X, type LucideIcon } from 'lucide-react';

export type AppDialogVariant = 'default' | 'warning' | 'destructive' | 'info';

export interface AppDialogAction {
    label: string;
    onClick: () => void;
    variant?: 'primary' | 'secondary' | 'destructive';
    disabled?: boolean;
    autoFocus?: boolean;
}

export interface AppDialogProps {
    open: boolean;
    title: string;
    message: React.ReactNode;
    variant?: AppDialogVariant;
    icon?: LucideIcon;
    onClose: () => void;
    actions: AppDialogAction[];
    /** Optional body below the message (e.g. copyable input). */
    children?: React.ReactNode;
    /** When true, Escape / overlay click do not close. */
    busy?: boolean;
}

const VARIANT_STYLES: Record<
    AppDialogVariant,
    { border: string; iconWrap: string; icon: string; callout: string }
> = {
    default: {
        border: 'border-accent-electric/30',
        iconWrap: 'bg-accent-electric/20 text-accent-electric',
        icon: 'text-accent-electric',
        callout: 'bg-accent-electric/10 border-accent-electric/20',
    },
    warning: {
        border: 'border-amber-500/30',
        iconWrap: 'bg-amber-500/20 text-amber-400',
        icon: 'text-amber-400',
        callout: 'bg-amber-500/10 border-amber-500/20',
    },
    destructive: {
        border: 'border-red-500/30',
        iconWrap: 'bg-red-500/20 text-red-400',
        icon: 'text-red-400',
        callout: 'bg-red-500/10 border-red-500/20',
    },
    info: {
        border: 'border-divider-subtle',
        iconWrap: 'bg-surface-active text-text-secondary',
        icon: 'text-text-secondary',
        callout: 'bg-surface-hover border-divider-subtle',
    },
};

function actionClassName(variant: AppDialogAction['variant']): string {
    switch (variant) {
        case 'destructive':
            return 'bg-red-500 text-white hover:bg-red-500/90 shadow-[0_0_20px_rgba(239,68,68,0.3)]';
        case 'secondary':
            return 'bg-surface-hover text-text-primary hover:bg-surface-active';
        case 'primary':
        default:
            return 'bg-accent-electric text-black hover:bg-accent-electric/90';
    }
}

/**
 * Themed modal shell used by DialogProvider and feature-specific confirms.
 */
export const AppDialog: React.FC<AppDialogProps> = ({
    open,
    title,
    message,
    variant = 'default',
    icon: Icon,
    onClose,
    actions,
    children,
    busy = false,
}) => {
    const titleId = useId();
    const styles = VARIANT_STYLES[variant];
    const DefaultIcon = variant === 'info' ? Info : AlertTriangle;
    const ResolvedIcon = Icon ?? DefaultIcon;
    const primaryRef = useRef<HTMLButtonElement>(null);

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && !busy) {
                event.preventDefault();
                onClose();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, busy, onClose]);

    useEffect(() => {
        if (!open) return;
        primaryRef.current?.focus();
    }, [open]);

    if (!open) return null;

    const requestClose = () => {
        if (!busy) onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[11000] flex items-center justify-center bg-bg-primary/80 backdrop-blur-sm p-4 animate-in fade-in"
            role="presentation"
            onClick={requestClose}
        >
            <div
                className={`glass w-full max-w-md max-h-[85dvh] flex flex-col rounded-3xl border ${styles.border} shadow-2xl relative overflow-hidden animate-in fade-in zoom-in duration-200`}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                onClick={(e) => e.stopPropagation()}
            >
                {/* WEB-114 increment 10: capped panel, scrolling body, the actions in a footer outside the scroll. */}
                <div className="shrink-0 p-6 pb-0 flex justify-between items-start gap-3">
                    <div className="flex items-start gap-3 min-w-0">
                        <div className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 ${styles.iconWrap}`}>
                            <ResolvedIcon size={22} className={styles.icon} />
                        </div>
                        <div className="min-w-0 pt-1">
                            <h2 id={titleId} className="text-xl font-bold text-text-primary leading-tight">
                                {title}
                            </h2>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={requestClose}
                        disabled={busy}
                        className="p-2 hover:bg-surface-active rounded-full transition-colors text-text-muted hover:text-text-primary disabled:opacity-40"
                        aria-label="Close"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="flex-1 min-h-0 overflow-y-auto p-6 pb-2 space-y-4">
                    <div className={`p-4 rounded-2xl border flex gap-3 items-start ${styles.callout}`}>
                        <p className="text-sm text-text-primary leading-relaxed">{message}</p>
                    </div>

                    {children}
                </div>

                <div className="shrink-0 px-6 pb-6 pt-2">
                    <div className="flex gap-3">
                        {actions.map((action) => (
                            <button
                                key={action.label}
                                ref={action.autoFocus ? primaryRef : undefined}
                                type="button"
                                onClick={action.onClick}
                                disabled={busy || action.disabled}
                                className={`flex-1 py-3 rounded-xl font-bold text-sm transition-colors disabled:opacity-40 ${actionClassName(action.variant ?? 'secondary')}`}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
